package ai.wagemix.web.components

import ai.wagemix.web.theme.WMSparklineHeightVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.compose.dom.svg.Polyline
import com.varabyte.kobweb.compose.dom.svg.SVGFillType
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeLineCap
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeLineJoin
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeType
import com.varabyte.kobweb.compose.dom.svg.SVGSvgAttrsScope
import com.varabyte.kobweb.compose.dom.svg.Svg
import org.jetbrains.compose.web.css.DisplayStyle

/**
 * Where [WMSparkline] puts each median in its `viewBox`: years run left to right across [WIDTH], and the medians'
 * range fills [HEIGHT] less [INSET] at the top and bottom so the stroke is never clipped. A flat line sits in the
 * middle. Points without a median are skipped.
 */
internal object WMSparklineGeometry {
    const val WIDTH = 100.0
    const val HEIGHT = 40.0
    const val INSET = 2.0

    /** The polyline's points; empty when fewer than two years have a median. */
    fun pointsOf(model: WMBandChartModel): List<Pair<Double, Double>> {
        val known = model.points.mapNotNull { point -> point.median?.let { point.year to it } }
        if (known.size < 2) {
            return emptyList()
        }
        val firstYear = known.first().first
        val yearSpan = (known.last().first - firstYear).coerceAtLeast(1)
        val low = known.minOf { it.second }
        val high = known.maxOf { it.second }
        return known.map { (year, median) ->
            val x = WIDTH * (year - firstYear) / yearSpan
            val y = if (high == low) {
                HEIGHT / 2
            } else {
                INSET + (HEIGHT - 2 * INSET) * (high - median) / (high - low)
            }
            x to y
        }
    }
}

/**
 * Penpot `Wagemix / Sparkline`: the median line alone, 56px tall, on saved-mix cards. Drawn as inline SVG rather than
 * an fs-charts embed: every embed is its own iframe and WebGL context, and a grid of cards would run out of contexts.
 */
@Composable
fun WMSparkline(model: WMBandChartModel, description: String, modifier: Modifier = Modifier) {
    val colors = ColorMode.current.toPalette().fs
    val points = remember(model) { WMSparklineGeometry.pointsOf(model) }
    Svg(
        attrs = Modifier.fillMaxWidth()
            .height(WMSparklineHeightVar.value())
            .display(DisplayStyle.Block)
            .color(colors.primary)
            .then(modifier)
            .toAttrs<SVGSvgAttrsScope> {
                viewBox(0, 0, WMSparklineGeometry.WIDTH, WMSparklineGeometry.HEIGHT)
                // Stretch to the card's width; the stroke keeps its width (vector-effect below).
                attr("preserveAspectRatio", "none")
                attr("role", "img")
                attr("aria-label", description)
            }
    ) {
        if (points.isNotEmpty()) {
            Polyline {
                points(*points.toTypedArray())
                fill(SVGFillType.None)
                stroke(SVGStrokeType.CurrentColor)
                strokeWidth(2)
                strokeLineCap(SVGStrokeLineCap.Round)
                strokeLineJoin(SVGStrokeLineJoin.Round)
                attr("vector-effect", "non-scaling-stroke")
            }
        }
    }
}
